import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface HorizontalScrollContainerProps {
  /** Title for the section (optional) */
  title?: string;
  /** Content to render inside the scroll container */
  children: React.ReactNode;
  /** Additional className for the scroll container */
  className?: string;
  /** Show scroll arrows (default: true) */
  showArrows?: boolean;
  /** Gap between items (default: gap-3) */
  gap?: string;
}

/**
 * Horizontal scroll container with navigation arrows.
 * Similar styling to FavouritesSection scroll behavior.
 */
export const HorizontalScrollContainer: React.FC<HorizontalScrollContainerProps> = ({
  title,
  children,
  className = '',
  showArrows = true,
  gap = 'gap-3',
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll state
  const checkScrollState = () => {
    const container = scrollContainerRef.current;
    if (container) {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    }
  };

  useEffect(() => {
    checkScrollState();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollState);
      window.addEventListener('resize', checkScrollState);
      return () => {
        container.removeEventListener('scroll', checkScrollState);
        window.removeEventListener('resize', checkScrollState);
      };
    }
  }, [children]);

  const scroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (container) {
      const scrollAmount = container.clientWidth * 0.8;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const showScrollButtons = showArrows && (canScrollLeft || canScrollRight);

  return (
    <div className="w-full">
      {/* Title and scroll buttons */}
      <div className="flex items-center justify-between mb-3">
        {title && (
          <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">{title}</h4>
        )}
        {!title && <div />}
        
        {showScrollButtons && (
          <div className="flex gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`w-7 h-7 flex items-center justify-center rounded-full transition-all ${
                canScrollLeft 
                  ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                  : 'bg-gray-800/30 text-gray-600 cursor-not-allowed'
              }`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`w-7 h-7 flex items-center justify-center rounded-full transition-all ${
                canScrollRight 
                  ? 'bg-gray-800 hover:bg-gray-700 text-white' 
                  : 'bg-gray-800/30 text-gray-600 cursor-not-allowed'
              }`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Scrollable container */}
      <div
        ref={scrollContainerRef}
        className={`flex ${gap} overflow-x-auto py-1 -my-1 px-1 -mx-1 pb-2 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent ${className}`}
        style={{ scrollbarWidth: 'thin' }}
      >
        {children}
      </div>
    </div>
  );
};

export default HorizontalScrollContainer;
